import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import router from '../../versa/router.js';
import record from '../../versa/record.js';
import formset from '../../versa/formset.js';
import { notify, ask } from '../../versa/msgbox.js';
import view from '../views/customer-routes.js';
import form from '../forms/customer-routes.js';
import addCustomersForm from '../forms/customer-routes-add-customers.js';
import * as types from '../types.js';

function downloadFile(base64Content, filename, mimeType) {
	const bin = atob(base64Content);
	const arr = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
	const blob = new Blob([arr], { type: mimeType });
	const a = document.createElement('a');
	a.href = URL.createObjectURL(blob);
	a.download = filename || 'export';
	a.click();
	URL.revokeObjectURL(a.href);
}

function customerStatusLabel(isDisabled) {
	return isDisabled === true || isDisabled === 1 || isDisabled === '1' ? 'Inativo' : 'Ativo';
}

function routeLabel(code, name) {
	if (!code && !name) return '—';
	return [code, name].filter(Boolean).join(' — ');
}

const customerRoutes = function ()
{
	const rt_crud = crud.create({
		addFilter(params) {
			crud.addFilter.call(this, params);
			if (this.filter && JSON.stringify(this.filter).includes('is_disabled'))
				params.append('include_disabled', '1');
		},
		async loadBaseData()
		{
			api.call(api.list_customer_routes, this.queryAll()).then((data) => db.set('customer_routes', data).catch(e => notify(e)));
			api.call(api.list_companies, this.queryAll()).then((data) => db.set('companies', data).catch(e => notify(e)));
		},
		async queryData(query)
		{
			return await api.call(api.list_customer_routes, query);
		},
		async loadRecord(id)
		{
			return await api.call(api.select_customer_route, ...JSON.parse(id));
		},
		editRecord(data)
		{
			record.edit(this.record_form, this.saveRecordWrapper.bind(this), data);
		},
		async setDisabled(disabled) {
			const ids = await this.getSelectedIds(['company_id', 'id']);
			if (!ids.length) return notify('Selecione ao menos uma rota.', 'Rotas');
			const action = disabled ? 'Inativar' : 'Ativar';
			ask(`Confirma ${action} ${ids.length} rota(s)?`, action, async (answer) => {
				if (answer !== 'Y') return;
				for (const [company_id, id] of ids) {
					const resp = await api.call(api.patch_customer_route, company_id, id, { is_disabled: !!disabled });
					if (resp == null) { notify('Falha ao atualizar rota.', 'Rotas'); return; }
				}
				this.loadPage();
			});
		},
		async saveRecord(data, old_data)
		{
			const _record = {
				code: String(data.code || '').trim(),
				name: String(data.name || '').trim(),
				description: String(data.description || '').trim() || null,
				is_disabled: data.is_disabled == '1',
			};
			if (old_data)
				return await api.call(api.update_customer_route, data.company_id, data.id, _record);
			const { is_disabled, ...insertBody } = _record;
			return await api.call(api.insert_customer_route, data.company_id, insertBody);
		},
		async deleteSelected()
		{
			const ids = await this.getSelectedIds(['company_id', 'id']);
			if (!ids.length)
				return notify('Selecione ao menos um registro.', 'Rotas');
			ask(
				`Excluir ${ids.length} rota(s)? Só é possível excluir rotas sem clientes vinculados.`,
				'Excluir',
				async (answer) =>
				{
					if (answer !== 'Y') return;
					for (const [company_id, id] of ids)
					{
						const resp = await api.call(api.delete_customer_route, company_id, id);
						if (resp === undefined) return;
					}
					this.reloadPage();
				}
			);
		},
		async exportRoutes(format) {
			try {
				const query = this.queryAll();
				const resp = await api.call(api.export_customer_routes, query, format);
				if (resp?.csv) {
					downloadFile(resp.csv, resp.filename || 'rotas.csv', 'text/csv;charset=utf-8');
				} else if (resp?.xlsx) {
					downloadFile(resp.xlsx, resp.filename || 'rotas.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
				} else {
					notify('Nenhum dado para exportar.', 'Aviso');
					return;
				}
				notify('Download iniciado.', 'Rotas');
			} catch (e) {
				notify(e?.message || String(e), 'Erro');
			}
		},
	});

	async function renderRouteCustomers(fs) {
		const root = document.querySelector('#customer_route');
		const section = root?.querySelector('#crc_customers_section');
		const tbody = root?.querySelector('#crc_customers_tbody');
		const hint = root?.querySelector('#crc_inactive_hint');
		const btnAdd = root?.querySelector('#crc_btn_add');
		if (!section || !tbody) return;

		const companyId = fs.form.getValues()?.company_id;
		const routeId = fs.form.getValues()?.id;
		const isDisabled = fs.form.getValues()?.is_disabled == '1';

		if (!companyId || !routeId) {
			section.style.display = 'none';
			return;
		}
		section.style.display = '';
		if (hint) hint.style.display = isDisabled ? '' : 'none';
		if (btnAdd) btnAdd.disabled = !!isDisabled;

		try {
			const rows = await api.call(api.list_customer_route_customers, companyId, routeId) || [];
			tbody.innerHTML = rows.map((r) => `
				<tr data-customer-id="${r.customer_id}">
					<td>${(r.customer_cnpj ?? r.customer_id ?? '').toString()}</td>
					<td>${(r.customer_nick ?? '').toString()}</td>
					<td>${(r.customer_name ?? '').toString()}</td>
					<td>${(r.city_name ?? '').toString()}</td>
					<td>${customerStatusLabel(r.is_disabled)}</td>
					<td><button type="button" class="button crc-btn-remove" data-id="${r.customer_id}">Remover</button></td>
				</tr>
			`).join('');
		} catch (e) {
			tbody.innerHTML = '';
			notify(e?.message || String(e), 'Erro');
		}
	}

	function bindRouteCustomersPanel(fs) {
		const root = document.querySelector('#customer_route');
		if (!root || root.dataset.crcBound === '1') return;
		root.dataset.crcBound = '1';

		const companyId = () => fs.form.getValues()?.company_id;
		const routeId = () => fs.form.getValues()?.id;
		const isDisabled = () => fs.form.getValues()?.is_disabled == '1';

		root.querySelector('#crc_btn_export_csv')?.addEventListener('click', async () => {
			try {
				const resp = await api.call(api.export_customer_route_customers, companyId(), routeId(), 'csv');
				if (resp?.csv) downloadFile(resp.csv, resp.filename, 'text/csv;charset=utf-8');
				else notify('Nenhum dado para exportar.', 'Aviso');
			} catch (e) { notify(e?.message || String(e), 'Erro'); }
		});
		root.querySelector('#crc_btn_export_xlsx')?.addEventListener('click', async () => {
			try {
				const resp = await api.call(api.export_customer_route_customers, companyId(), routeId(), 'xlsx');
				if (resp?.xlsx) downloadFile(resp.xlsx, resp.filename, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
				else notify('Nenhum dado para exportar.', 'Aviso');
			} catch (e) { notify(e?.message || String(e), 'Erro'); }
		});
		root.querySelector('#crc_btn_add')?.addEventListener('click', () => openAddCustomersModal(fs));
		root.querySelector('#crc_customers_tbody')?.addEventListener('click', async (ev) => {
			const btn = ev.target.closest('.crc-btn-remove');
			if (!btn) return;
			const customerId = Number(btn.dataset.id);
			ask('Remover cliente desta rota?', 'Remover', async (answer) => {
				if (answer !== 'Y') return;
				try {
					await api.call(api.unlink_customer_route_customer, companyId(), routeId(), customerId);
					notify('Cliente removido da rota.', 'Rotas');
					await renderRouteCustomers(fs);
				} catch (e) { notify(e?.message || String(e), 'Erro'); }
			});
		});
		root.querySelector('#is_disabled')?.addEventListener('change', () => {
			const hint = root.querySelector('#crc_inactive_hint');
			const btnAdd = root.querySelector('#crc_btn_add');
			const disabled = isDisabled();
			if (hint) hint.style.display = disabled ? '' : 'none';
			if (btnAdd) btnAdd.disabled = disabled;
		});
	}

	function openAddCustomersModal(fs) {
		if (isRouteInactive(fs)) {
			notify('Rota inativa não permite adicionar clientes.', 'Rotas');
			return;
		}
		const companyId = fs.form.getValues()?.company_id;
		const routeId = fs.form.getValues()?.id;
		let searchTimer = null;
		let availableRows = [];

		const dlg = formset.create(addCustomersForm, async () => dlg.destroy());
		const tbody = document.querySelector('#crc-add-tbody');
		const search = document.querySelector('#crc_search');

		async function refreshSearch() {
			const q = search?.value?.trim() || '';
			try {
				availableRows = await api.call(api.search_customer_route_available, companyId, routeId, q) || [];
				if (!tbody) return;
				tbody.innerHTML = availableRows.map((r) => {
					const onRoute = r.current_route_id === routeId;
					const otherRoute = r.current_route_id && r.current_route_id !== routeId;
					const routeTxt = onRoute
						? 'Já nesta rota'
						: (otherRoute ? routeLabel(r.current_route_code, r.current_route_name) : 'Sem rota');
					return `
						<tr>
							<td><input type="checkbox" class="crc-pick" value="${r.id}"${onRoute ? ' disabled' : ''}></td>
							<td>${(r.cnpj ?? r.id ?? '').toString()}</td>
							<td>${(r.nick ?? '').toString()}</td>
							<td>${(r.name ?? '').toString()}</td>
							<td>${routeTxt}</td>
						</tr>`;
				}).join('');
			} catch (e) {
				notify(e?.message || String(e), 'Erro');
			}
		}

		search?.addEventListener('input', () => {
			clearTimeout(searchTimer);
			searchTimer = setTimeout(refreshSearch, 300);
		});
		document.querySelector('.bn_crc_cancel')?.addEventListener('click', () => dlg.destroy());
		document.querySelector('.bn_crc_confirm')?.addEventListener('click', async () => {
			const picks = [...document.querySelectorAll('#crc-add-tbody .crc-pick:checked')].map((el) => Number(el.value));
			if (!picks.length) {
				notify('Selecione ao menos um cliente.', 'Rotas');
				return;
			}
			const needsMove = picks.filter((id) => {
				const row = availableRows.find((r) => r.id === id);
				return row?.current_route_id && row.current_route_id !== routeId;
			});
			const doLink = async (allowMove) => {
				try {
					const result = await api.call(api.link_customer_route_customers, companyId, routeId, {
						customer_ids: picks,
						allow_move: allowMove,
					});
					const msgs = [];
					if (result.linked) msgs.push(`${result.linked} vinculado(s)`);
					if (result.already_linked) msgs.push(`${result.already_linked} já vinculado(s)`);
					if (result.moved) msgs.push(`${result.moved} movido(s) para a rota`);
					if (result.conflicts?.length) {
						const parts = [];
						if (result.linked) parts.push(`${result.linked} vinculado(s)`);
						if (result.already_linked) parts.push(`${result.already_linked} já na rota`);
						if (parts.length) notify(parts.join('; '), 'Rotas');
						notify('Cliente pertence a outra rota. Selecione novamente e confirme a movimentação.', 'Rotas');
						await renderRouteCustomers(fs);
						return;
					}
					if (msgs.length) notify(msgs.join('; '), 'Rotas');
					else notify('Cliente já vinculado à rota.', 'Rotas');
					dlg.destroy();
					await renderRouteCustomers(fs);
				} catch (e) {
					notify(e?.message || String(e), 'Erro');
				}
			};
			if (needsMove.length) {
				ask(
					`${needsMove.length} cliente(s) pertence(m) a outra rota. Deseja movê-los para esta rota?`,
					'Mover clientes',
					async (answer) => {
						if (answer === 'Y') await doLink(true);
					},
				);
				return;
			}
			await doLink(false);
		});
		setTimeout(refreshSearch, 100);
	}

	function isRouteInactive(fs) {
		return fs.form.getValues()?.is_disabled == '1';
	}

	window.__crcRouteCustomersInit = (fs) => {
		bindRouteCustomersPanel(fs);
		renderRouteCustomers(fs);
	};

	router.set('#customer-route-delete', rt_crud.deleteSelected.bind(rt_crud));
	router.set('#customer-route-disable', rt_crud.setDisabled.bind(rt_crud, true));
	router.set('#customer-route-enable', rt_crud.setDisabled.bind(rt_crud, false));
	router.set('#customer-route-export-csv', () => rt_crud.exportRoutes('csv'));
	router.set('#customer-route-export-xlsx', () => rt_crud.exportRoutes('xlsx'));

	const fieldspecs = [
		{ index: 1, field: 'company_id', label: 'Empresa', types: [], placeholder: '', title: 'Empresa', store: 'companies', labels: ['nick'] },
		{ index: 2, field: 'id', label: 'Id', types: [], placeholder: '', title: 'Identificador' },
		{ index: 3, field: 'code', label: 'Código', types: [], placeholder: '', title: 'Código da rota' },
		{ index: 4, field: 'name', label: 'Nome', types: [], placeholder: '', title: 'Nome da rota' },
		{ index: 5, field: 'description', label: 'Descrição', types: [], placeholder: '', title: 'Descrição opcional' },
		{ index: 6, field: 'is_disabled', label: 'Inativo', types: [types.opt_bool(), types.datalist()], placeholder: '', title: 'Rota inativa' },
	];

	const operations = [
		{ hash: '#customer-route-export-csv', label: 'Exportar&nbsp;CSV' },
		{ hash: '#customer-route-export-xlsx', label: 'Exportar&nbsp;XLSX' },
		{ hash: '#customer-route-disable', label: 'Inativar' },
		{ hash: '#customer-route-enable', label: 'Ativar' },
		{ hash: '#customer-route-delete', label: 'Excluir' },
	];

	rt_crud.run(
		'customer_routes',
		'Rotas',
		document.getElementById('workarea'),
		view,
		form,
		fieldspecs,
		operations
	);
};

export default customerRoutes;
